import React from "react";
import "react-toastify/dist/ReactToastify.css";
import DataTable, { createTheme } from "react-data-table-component";
import { useTheme } from "../../../context/ThemeContext";
import TableLoader from "../../skeletons/TableLoader";
import { checkPermission, formatAMPM } from "../../../helpers/GeneralHelper";
import dateFormat from "dateformat";
import { getCmsDataTableStyles } from "../../../helpers/DataTableStyles";

const formatMeetingTime = (value) => {
  if (!value || value === "00:00:00") return "";
  const text = String(value).trim();
  if (!text || text.includes("NaN")) return "";

  if (value instanceof Date && !isNaN(value.getTime())) {
    return formatAMPM(value);
  }

  const match24 = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (match24) {
    const date = new Date(
      2024,
      0,
      1,
      Number(match24[1]),
      Number(match24[2]),
      0,
      0
    );
    return isNaN(date.getTime()) ? "" : formatAMPM(date);
  }

  const parsed = new Date(Date.parse(`2024-01-01 ${text}`));
  return isNaN(parsed.getTime()) ? "" : formatAMPM(parsed);
};

export default function MeetingTable({
  module,
  data,
  loading,
  totalRows,
  page,
  limit,
  fetchData,
  openUpdateModal,
  openDeleteModal,
  handleView,
}) {
  const columns = [
    {
      name: "#",
      selector: (row, index) => <div>{(page - 1) * limit + index + 1}.</div>,
      width: "80px",
    },
    {
      name: "Account",
      selector: (row) => row.client_name || "-",
      sortable: true,
    },
    {
      name: "POC",
      selector: (row) => row.contact_name || "-",
      sortable: true,
    },
    {
      name: "Date",
      selector: (row) =>
        row.meeting_date ? dateFormat(row.meeting_date, "dd/mm/yyyy") : "-",
      sortable: true,
    },
    {
      name: "Time",
      selector: (row) => {
        const start = formatMeetingTime(row.meeting_start_time);
        const end = formatMeetingTime(row.meeting_end_time);
        if (!start && !end) return "-";
        return end ? `${start} - ${end}` : start;
      },
    },
    {
      name: "Type",
      selector: (row) => row.meeting_type || "-",
      sortable: true,
    },
    {
      name: "Mode",
      selector: (row) => row.meeting_mode || "-",
      sortable: true,
    },
    {
      name: "Account Manager",
      selector: (row) =>
        row.logged_by_name ||
        row.user_name ||
        [row.user_first_name, row.user_last_name].filter(Boolean).join(" ") ||
        "-",
      sortable: true,
    },
  ];

  if (
    checkPermission(module.route, "edit") ||
    checkPermission(module.route, "delete")
  ) {
    columns.push({
      name: "Action",
      right: "true",
      cell: (row) => (
        <div className="hstack gap-2 fs-15">
          {checkPermission(module.route, "edit") && (
            <button
              type="button"
              className="btn btn-icon btn-sm btn-info"
              title="Edit"
              onClick={() => openUpdateModal(row)}
            >
              <i className="bx bxs-edit"></i>
            </button>
          )}
          {checkPermission(module.route, "delete") && (
            <button
              type="button"
              className="btn btn-icon btn-sm btn-danger"
              title="Delete"
              onClick={() => openDeleteModal(row)}
            >
              <i className="bx bxs-trash"></i>
            </button>
          )}
        </div>
      ),
    });
  }

  const { theme } = useTheme();

  const handlePageChange = async (page) => {
    fetchData(limit, page);
  };

  const handleLimitChange = async (newLimit, page) => {
    fetchData(newLimit, page);
  };

  createTheme("dark", {
    text: {
      primary: "#a2a3a4",
      secondary: "#2aa198",
    },
    background: {
      default: "#252729",
    },
    context: {
      background: "#cb4b16",
      text: "#a2a3a4",
    },
    divider: {
      default: "#073642",
    },
    action: {
      button: "rgba(0,0,0,.54)",
      hover: "rgba(0,0,0,.08)",
      disabled: "rgba(0,0,0,.12)",
    },
  });

  return (
    <DataTable
      className="clickable cms-data-table"
      onRowClicked={(row) => handleView(row)}
      columns={columns}
      data={data}
      pagination
      paginationServer
      paginationTotalRows={totalRows}
      onChangeRowsPerPage={handleLimitChange}
      onChangePage={handlePageChange}
      progressPending={loading}
      progressComponent={<TableLoader noOfColumn={columns.length} />}
      fixedHeader
      fixedHeaderScrollHeight="550px"
      highlightOnHover
      paginationPerPage={10}
      paginationRowsPerPageOptions={[10, 25, 50, 100, totalRows]}
      theme={`${theme === "light" ? "light" : "dark"}`}
      customStyles={getCmsDataTableStyles(theme)}
    />
  );
}
